// How a sheet board is kept: one entry per tab (its name, column widths, row heights, frozen rows and columns, and the
// looks of whole columns and rows), and its cells in bands of 50 rows, one entry each inside the tab (t: "cells"),
// each a map from A1 address to what was typed ("12", "=SUM(A1:A3)", "Hello") and each cell's look.
//
// Why bands: a tab of 5,000 rows by 52 columns is several megabytes, more than one entry can carry, and a change
// writes only the band it touches. Why a patch: a change sends only the cells it changed (`set`, `fset`), which the
// server lays over the band it has (shared/entries.ts cleans every write through cleanCells), so the user typing in
// one cell and an agent writing another at the same moment both keep their cells.

import { BAND, MAX_COLS, MAX_ROWS, addrOf, bandOf, colIndex, colName, parseAddr, type Rect } from "./addr";
import { cleanFmt, type CellFmt } from "./format";
import type { Entry, ID } from "../types";
import { regionDayFirst } from "../region";
import { cleanNames } from "./names";
import { cleanColorRules, cleanValid, type ColorRule, type ValidRule } from "./rules";
import { cleanHidden, cleanMerges } from "./layout";
import { cleanTables, type SheetTable } from "./tables";
import { cleanCharts, type SheetChart } from "./charts";

/** A cell holds up to this many characters, as in Google Sheets. */
export const MAX_CELL = 50_000;
/** A band of cells, as JSON: well under what the board's storage carries for a row. */
export const MAX_BAND_BYTES = 600_000;
export const MAX_TAB_NAME = 100;
export const MAX_TABS = 40;
export const DEFAULT_W = 100;
export const MIN_W = 24;
export const MAX_W = 1200;

export interface SheetTabData {
  name: string;
  /** column letters to widths in pixels, for those not the default */
  cols?: Record<string, number>;
  /** row numbers (from 1) to heights chosen by hand */
  rows?: Record<string, number>;
  /** rows and columns frozen at the top and left */
  frozen?: { r: number; c: number };
  /** whole columns' and rows' looks (a column formatted as currency) */
  colFmt?: Record<string, CellFmt>;
  rowFmt?: Record<string, CellFmt>;
  /**
   * dates typed day first (25/10/2026, true) or month first (10/25/2026, false): set on every tab when the sheet is
   * made, from the account's region (regionDayFirst), and changed in Number format. A sheet made before 28 Oct may
   * have none, or only true: it follows the account's region (sheetDayFirst) until its owner opens it, which sets it.
   */
  dmy?: boolean;
  /** named ranges whose cells are on this tab (Fixes 9, names.ts): name to range, "B2:B10" */
  names?: Record<string, string>;
  /** checkboxes and dropdowns on blocks of the tab (Fixes 9, rules.ts) */
  valid?: ValidRule[];
  /** colours by rule on blocks of the tab (Fixes 9, rules.ts) */
  cf?: ColorRule[];
  /** rows (numbers from 1) and columns (letters) hidden from view (Fixes 9, H2b, layout.ts): their cells read and
   *  write as ever */
  hideRows?: Record<string, 1>;
  hideCols?: Record<string, 1>;
  /** blocks of cells shown as one, the top left cell's value across them (Fixes 9, H2b): "A1:D1" */
  merges?: string[];
  /** tables (Features 5, tables.ts): a named block with a header row, its columns' formulas and looks */
  tables?: SheetTable[];
  /** charts of blocks of the tab, beside its cells (Fixes 9, H2b, charts.ts) */
  charts?: SheetChart[];
}

export interface CellsData {
  band: number;
  cells: Record<string, string>;
  fmt?: Record<string, CellFmt>;
}

/** A change to a band: only the cells it changes, null or "" to empty one. */
export interface CellsPatch {
  band: number;
  set?: Record<string, string | null>;
  fset?: Record<string, CellFmt | null>;
}

export class SheetRefused extends Error {}

const refuse = (msg: string): never => {
  throw new SheetRefused(msg);
};

/** A tab's name, on one line: never empty, never too long, never with characters a reference cannot carry. */
export function cleanTabName(raw: unknown, fallback = "Sheet 1"): string {
  const s = typeof raw === "string" ? raw.replace(/[\r\n\t]+/g, " ").replace(/[!]/g, "").replace(/\s+/g, " ").trim().slice(0, MAX_TAB_NAME) : "";
  return s || fallback;
}

function cleanSizes(raw: unknown, key: (k: string) => boolean, min: number, max: number): Record<string, number> | undefined {
  if (!raw || typeof raw !== "object") return undefined;
  const out: Record<string, number> = {};
  for (const [k, v] of Object.entries(raw as Record<string, unknown>)) if (key(k) && typeof v === "number" && Number.isFinite(v)) out[k] = Math.round(Math.max(min, Math.min(max, v)));
  return Object.keys(out).length ? out : undefined;
}

function cleanFmts(raw: unknown, key: (k: string) => boolean): Record<string, CellFmt> | undefined {
  if (!raw || typeof raw !== "object") return undefined;
  const out: Record<string, CellFmt> = {};
  for (const [k, v] of Object.entries(raw as Record<string, unknown>)) {
    if (!key(k)) continue;
    // a whole column's or row's look never carries a link: links are a cell's own
    const f = cleanFmt(v, false);
    if (f) out[k] = f;
  }
  return Object.keys(out).length ? out : undefined;
}

const isCol = (k: string) => {
  const c = colIndex(k);
  return c >= 0 && c < MAX_COLS && k === k.toUpperCase();
};
const isRow = (k: string) => /^[1-9][0-9]{0,4}$/.test(k) && Number(k) <= MAX_ROWS;

/** A tab's fields, each in range; a field left out keeps what the tab had, null clears it. */
export function cleanSheetTab(raw: Record<string, unknown>, before: SheetTabData | null): SheetTabData {
  const name = "name" in raw ? cleanTabName(raw.name, before?.name ?? "Sheet 1") : (before?.name ?? "Sheet 1");
  const pick = <T,>(k: keyof SheetTabData, clean: (v: unknown) => T | undefined): T | undefined => (k in raw ? (raw[k] === null ? undefined : clean(raw[k])) : (before?.[k] as T | undefined));
  const cols = pick("cols", (v) => cleanSizes(v, isCol, MIN_W, MAX_W));
  const rows = pick("rows", (v) => cleanSizes(v, isRow, 16, 600));
  const frozen = pick("frozen", (v) => {
    const f = v as { r?: unknown; c?: unknown } | null;
    const r = typeof f?.r === "number" ? Math.max(0, Math.min(20, Math.round(f.r))) : 0;
    const c = typeof f?.c === "number" ? Math.max(0, Math.min(10, Math.round(f.c))) : 0;
    return r || c ? { r, c } : undefined;
  });
  const colFmt = pick("colFmt", (v) => cleanFmts(v, isCol));
  const rowFmt = pick("rowFmt", (v) => cleanFmts(v, isRow));
  // day first or month first, as the sheet was set; null clears it (the account's region then)
  const dmy = "dmy" in raw ? (typeof raw.dmy === "boolean" ? raw.dmy : undefined) : before?.dmy;
  const names = pick("names", cleanNames);
  const valid = pick("valid", cleanValid);
  const cf = pick("cf", cleanColorRules);
  const hideRows = pick("hideRows", (v) => cleanHidden(v, "row"));
  const hideCols = pick("hideCols", (v) => cleanHidden(v, "col"));
  const merges = pick("merges", cleanMerges);
  const tables = pick("tables", cleanTables);
  const charts = pick("charts", cleanCharts);
  return {
    name,
    ...(cols ? { cols } : {}),
    ...(rows ? { rows } : {}),
    ...(frozen ? { frozen } : {}),
    ...(colFmt ? { colFmt } : {}),
    ...(rowFmt ? { rowFmt } : {}),
    ...(typeof dmy === "boolean" ? { dmy } : {}),
    ...(names ? { names } : {}),
    ...(valid ? { valid } : {}),
    ...(cf ? { cf } : {}),
    ...(hideRows ? { hideRows } : {}),
    ...(hideCols ? { hideCols } : {}),
    ...(merges ? { merges } : {}),
    ...(tables ? { tables } : {}),
    ...(charts ? { charts } : {}),
  };
}

/** Whether an address is a cell in a band. */
function inBand(a: string, band: number): boolean {
  const p = parseAddr(a);
  return !!p && bandOf(p.r) === band && a === addrOf(p.r, p.c);
}

/**
 * A band's cells as kept: from a whole map (cells, fmt), or a patch laid over the band as the server has it (set,
 * fset), so two people writing different cells at once both keep theirs. Every address is a cell of the band.
 */
export function cleanCells(raw: Record<string, unknown>, before: CellsData | null): CellsData {
  const band = before ? before.band : typeof raw.band === "number" && Number.isInteger(raw.band) && raw.band >= 0 && raw.band < Math.ceil(MAX_ROWS / BAND) ? raw.band : refuse("A band of cells needs its number.");
  const text = (v: unknown, a: string): string | null => {
    if (v === null || v === undefined || v === "") return null;
    if (typeof v === "number" || typeof v === "boolean") v = String(v);
    if (typeof v !== "string") refuse(`${a} holds text, a number or a formula.`);
    if ((v as string).length > MAX_CELL) refuse(`${a} is too long: a cell holds up to ${MAX_CELL.toLocaleString("en-US")} characters.`);
    return v as string;
  };
  let cells: Record<string, string>;
  // a patch to a band the server does not have yet (its first write failed and went again): the patch is the band
  if (raw.set && typeof raw.set === "object") {
    cells = { ...(before?.cells ?? {}) };
    for (const [a, v] of Object.entries(raw.set as Record<string, unknown>)) {
      if (!inBand(a, band)) refuse(`${a} is not in rows ${band * BAND + 1} to ${band * BAND + BAND}.`);
      const t = text(v, a);
      if (t === null) delete cells[a];
      else cells[a] = t;
    }
  } else if (raw.cells && typeof raw.cells === "object") {
    cells = {};
    for (const [a, v] of Object.entries(raw.cells as Record<string, unknown>)) {
      if (!inBand(a, band)) refuse(`${a} is not in rows ${band * BAND + 1} to ${band * BAND + BAND}.`);
      const t = text(v, a);
      if (t !== null) cells[a] = t;
    }
  } else cells = { ...(before?.cells ?? {}) };
  let fmt: Record<string, CellFmt>;
  if (raw.fset && typeof raw.fset === "object") {
    fmt = { ...(before?.fmt ?? {}) };
    for (const [a, v] of Object.entries(raw.fset as Record<string, unknown>)) {
      if (!inBand(a, band)) refuse(`${a} is not in rows ${band * BAND + 1} to ${band * BAND + BAND}.`);
      const f = cleanFmt(v);
      if (f) fmt[a] = f;
      else delete fmt[a];
    }
  } else if ("fmt" in raw && !raw.set) {
    fmt = {};
    for (const [a, v] of Object.entries((raw.fmt ?? {}) as Record<string, unknown>)) {
      if (!inBand(a, band)) continue;
      const f = cleanFmt(v);
      if (f) fmt[a] = f;
    }
  } else fmt = { ...(before?.fmt ?? {}) };
  const out: CellsData = { band, cells, ...(Object.keys(fmt).length ? { fmt } : {}) };
  if (JSON.stringify(out).length > MAX_BAND_BYTES) refuse(`Rows ${band * BAND + 1} to ${band * BAND + BAND} hold too much text to save (the limit is ${MAX_BAND_BYTES / 1000} KB for 50 rows). Shorten some cells.`);
  return out;
}

// ---------------------------------------------------------------------------
// a sheet as the browser and the server read it

export interface SheetTab {
  entry: Entry;
  data: SheetTabData;
  /** its bands, by number; two saved for the same rows show as one, every cell of both in it */
  bands: Map<number, Entry>;
  /** bands saved for rows another band of the tab holds (two writers before the ids were the tab's): shown merged
   * into that band, and folded into it by the next write there */
  extra: Entry[];
}

/**
 * A band's id: its tab's and its number, so every writer that makes the band (the user, an agent, a coworker, two tabs)
 * makes the same entry, and the server lays their cells over each other in it. Within an entry id's 40 characters.
 */
export function bandId(tabId: ID, band: number): ID {
  const tail = `_b${band}`;
  return tabId.slice(0, 40 - tail.length) + tail;
}

export const tabData = (e: Pick<Entry, "data">): SheetTabData => e.data as unknown as SheetTabData;
export const cellsData = (e: Pick<Entry, "data">): CellsData => e.data as unknown as CellsData;

/** A sheet board's tabs in their order, each with its bands. */
export function sheetTabs(entries: Entry[]): SheetTab[] {
  const tabs = entries
    .filter((e) => e.t === "sheet")
    .sort((a, b) => a.order - b.order || a.createdAt - b.createdAt || (a.id < b.id ? -1 : 1))
    .map((entry): SheetTab => ({ entry, data: tabData(entry), bands: new Map(), extra: [] }));
  const byId = new Map(tabs.map((t) => [t.entry.id, t]));
  const many = new Map<SheetTab, Map<number, Entry[]>>();
  for (const e of entries) {
    if (e.t !== "cells" || !e.parent) continue;
    const t = byId.get(e.parent);
    if (!t) continue;
    const n = cellsData(e).band;
    const was = t.bands.get(n);
    if (!was) {
      t.bands.set(n, e);
      continue;
    }
    const m = many.get(t) ?? new Map<number, Entry[]>();
    many.set(t, m);
    m.set(n, [...(m.get(n) ?? [was]), e]);
  }
  // two bands saved for the same rows: one band, every cell of both, the later write winning a cell both hold; it
  // takes the id the tab's own band has (or the first made), so writes go there and fold the other in
  for (const [t, m] of many)
    for (const [n, list] of m) {
      const own = bandId(t.entry.id, n);
      const main = list.find((e) => e.id === own) ?? [...list].sort((a, b) => a.createdAt - b.createdAt || (a.id < b.id ? -1 : 1))[0];
      const cells: Record<string, string> = {};
      const fmt: Record<string, CellFmt> = {};
      for (const e of [...list].sort((a, b) => a.updatedAt - b.updatedAt || (a.id < b.id ? -1 : 1))) {
        Object.assign(cells, cellsData(e).cells);
        Object.assign(fmt, cellsData(e).fmt ?? {});
      }
      t.bands.set(n, { ...main, data: { band: n, cells, ...(Object.keys(fmt).length ? { fmt } : {}) } });
      t.extra.push(...list.filter((e) => e !== main));
    }
  return tabs;
}

/** The entries to fold away when a write reaches bands of a tab (all of them when bands is left out): its extras there. */
export function extrasOf(tab: SheetTab, bands?: Iterable<number>): Entry[] {
  if (!tab.extra.length) return [];
  const want = bands ? new Set(bands) : null;
  return tab.extra.filter((e) => !want || want.has(cellsData(e).band));
}

/** A tab's cell as typed, read from its bands (for tables' headers, before an engine has the cells). */
export function rawIn(tabs: SheetTab[]): (tabId: string, r: number, c: number) => string {
  return (tabId, r, c) => {
    const band = tabs.find((t) => t.entry.id === tabId)?.bands.get(bandOf(r));
    return band ? (cellsData(band).cells[addrOf(r, c)] ?? "") : "";
  };
}

/** A tab's cells, as [key, input] for the engine. */
export function* tabCells(tab: SheetTab): Generator<[string, string]> {
  for (const b of tab.bands.values()) for (const [a, raw] of Object.entries(cellsData(b).cells)) yield [a, raw];
}

/** The tab an agent or a person names: by its name (any case), else the first. */
export function findTab(tabs: SheetTab[], name: string | null | undefined): SheetTab | null {
  if (!name) return tabs[0] ?? null;
  const n = name.trim().toLowerCase();
  return tabs.find((t) => t.data.name.toLowerCase() === n) ?? null;
}

/** A name for a new tab that no tab has: "Sheet 2", "Sheet 3"… */
export function freshTabName(tabs: { data: { name: string } }[], base = "Sheet"): string {
  const taken = new Set(tabs.map((t) => t.data.name.toLowerCase()));
  if (base !== "Sheet" && !taken.has(base.toLowerCase())) return base;
  for (let n = base === "Sheet" ? tabs.length + 1 : 2; n < 1000; n++) {
    const name = `${base} ${n}`;
    if (!taken.has(name.toLowerCase())) return name;
  }
  return `${base} ${Date.now() % 10_000}`;
}

/** The cells in use on a tab: up to the last row and column holding something, or null for an empty tab. */
export function usedRect(tab: SheetTab): Rect | null {
  let r1 = -1;
  let c1 = -1;
  for (const [a] of tabCells(tab)) {
    const p = parseAddr(a);
    if (!p) continue;
    if (p.r > r1) r1 = p.r;
    if (p.c > c1) c1 = p.c;
  }
  return r1 < 0 ? null : { r0: 0, c0: 0, r1, c1 };
}

export function colWidth(t: SheetTabData, c: number): number {
  return t.cols?.[colName(c)] ?? DEFAULT_W;
}

// ---------------------------------------------------------------------------
// writing cells: what a change to many cells is, band by band

export interface CellWrite {
  r: number;
  c: number;
  /** what to type there; null empties it; undefined leaves it */
  raw?: string | null;
  /** its look; null clears it; undefined leaves it */
  fmt?: CellFmt | null;
}

/** Writes grouped by band: each band's patch. */
export function patchesFor(writes: CellWrite[]): Map<number, CellsPatch> {
  const out = new Map<number, CellsPatch>();
  for (const w of writes) {
    if (w.r < 0 || w.c < 0 || w.r >= MAX_ROWS || w.c >= MAX_COLS) continue;
    const band = bandOf(w.r);
    const p = out.get(band) ?? { band };
    const a = addrOf(w.r, w.c);
    if (w.raw !== undefined) (p.set ??= {})[a] = w.raw === "" ? null : w.raw;
    if (w.fmt !== undefined) (p.fset ??= {})[a] = w.fmt;
    out.set(band, p);
  }
  return out;
}

/** A band with a patch laid over it, as the server lays it (cleanCells), for what the browser shows at once. */
export function applyPatch(before: CellsData | null, p: CellsPatch): CellsData {
  return cleanCells(p as unknown as Record<string, unknown>, before ?? { band: p.band, cells: {} });
}

/**
 * The entry a change to a band is sent as: only the cells it changed, over the band as the server has it. A band
 * the server does not have yet goes whole.
 */
export function wireCells(before: Entry | null, after: Entry): Entry {
  if (after.t !== "cells") return after;
  // a band this tab made goes as its cells too, never as a whole band: another writer may have made it first
  if (!before) {
    const d = cellsData(after);
    return { ...after, data: { band: d.band, set: { ...d.cells }, ...(d.fmt && Object.keys(d.fmt).length ? { fset: { ...d.fmt } } : {}) } };
  }
  const a = cellsData(before);
  const b = cellsData(after);
  const set: Record<string, string | null> = {};
  for (const k of new Set([...Object.keys(a.cells), ...Object.keys(b.cells)])) if ((a.cells[k] ?? null) !== (b.cells[k] ?? null)) set[k] = b.cells[k] ?? null;
  const fset: Record<string, CellFmt | null> = {};
  const fa = a.fmt ?? {};
  const fb = b.fmt ?? {};
  for (const k of new Set([...Object.keys(fa), ...Object.keys(fb)])) if (JSON.stringify(fa[k] ?? null) !== JSON.stringify(fb[k] ?? null)) fset[k] = fb[k] ?? null;
  return { ...after, data: { band: b.band, set, ...(Object.keys(fset).length ? { fset } : {}) } };
}

/** Whether an entry put is a patch to a band (set or fset), to lay over the band rather than replace it. */
export function isCellsPatch(e: Pick<Entry, "t" | "data">): boolean {
  return e.t === "cells" && (!!e.data.set || !!e.data.fset) && !e.data.cells;
}

/** Two patches to one band as one, the later winning cell by cell. */
export function joinPatches(a: Entry, b: Entry): Entry {
  return { ...b, data: { band: b.data.band, set: { ...((a.data.set as object) ?? {}), ...((b.data.set as object) ?? {}) }, fset: { ...((a.data.fset as object) ?? {}), ...((b.data.fset as object) ?? {}) } } };
}

/**
 * A step's change to a tab or band taken back (or put forward again) over the tab as it is now: only the cells, sizes
 * and looks the step changed, never what was written since (src/state/actions.ts, undo and redo).
 */
export function shiftSheet(cur: Entry, from: Entry, to: Entry): Entry {
  const same = (x: unknown, y: unknown) => JSON.stringify(x ?? null) === JSON.stringify(y ?? null);
  const data: Record<string, unknown> = { ...cur.data };
  let changed = false;
  const maps = cur.t === "cells" ? ["cells", "fmt"] : ["cols", "rows", "colFmt", "rowFmt", "hideRows", "hideCols"];
  for (const k of new Set([...Object.keys(from.data), ...Object.keys(to.data), ...maps])) {
    if (k === "set" || k === "fset") continue;
    if (maps.includes(k)) {
      const f = (from.data[k] ?? {}) as Record<string, unknown>;
      const t = (to.data[k] ?? {}) as Record<string, unknown>;
      const m = { ...((cur.data[k] ?? {}) as Record<string, unknown>) };
      let hit = false;
      for (const key of new Set([...Object.keys(f), ...Object.keys(t)])) {
        if (same(f[key], t[key]) || same(m[key], t[key])) continue;
        if (t[key] === undefined) delete m[key];
        else m[key] = t[key];
        hit = true;
      }
      if (hit) {
        data[k] = m;
        changed = true;
      }
      continue;
    }
    if (same(from.data[k], to.data[k]) || same(data[k], to.data[k])) continue;
    data[k] = to.data[k] === undefined ? null : to.data[k];
    changed = true;
  }
  let { parent, order } = cur;
  if (from.order !== to.order && order !== to.order) {
    order = to.order;
    parent = to.parent;
    changed = true;
  }
  return changed ? { ...cur, parent, order, data } : cur;
}

/** A new band's entry, for a tab. */
/** A new band's entry, for a tab, with the band's own id (bandId). */
export function newBand(tab: Pick<Entry, "id" | "boardId">, band: number, now: number, data: Partial<CellsData> = {}): Entry {
  return { id: bandId(tab.id, band), boardId: tab.boardId, t: "cells", parent: tab.id, order: band, data: { band, cells: {}, ...data }, createdAt: now, updatedAt: now, by: null };
}

/** A band's change as a patch, cells and looks laid over what the band holds: never a whole band replacing another's. */
export function asPatch(data: Record<string, unknown>): Record<string, unknown> {
  if (data.set || data.fset) return data;
  return { band: data.band, set: { ...((data.cells as object) ?? {}) }, ...(data.fmt ? { fset: { ...(data.fmt as object) } } : {}) };
}

// How the region writes dates lives in shared/region.ts, which the Calendar board shares; kept here by name for the
// sheet's own callers
export { regionDayFirst };

/** Whether a sheet reads typed dates day first: as any of its tabs was set (the first that says), else the account's region. */
export function sheetDayFirst(tabs: { data: Pick<SheetTabData, "dmy"> }[], tz?: string | null): boolean {
  for (const t of tabs) if (typeof t.data.dmy === "boolean") return t.data.dmy;
  return regionDayFirst(tz);
}

/** Whether every tab of a sheet says how it reads dates (a sheet made before 28 Oct may not). */
export function dayOrderSet(tabs: { data: Pick<SheetTabData, "dmy"> }[]): boolean {
  return tabs.length > 0 && tabs.every((t) => typeof t.data.dmy === "boolean");
}
